
import React, { useMemo } from "react";
import {
  Card,
  CardContent
} from "../components/ui/card";
import { CheckCircle, AlertCircle, MinusCircle } from "lucide-react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from "recharts";

// Types
interface EpfoFiling {
  trrn: string;
  wage_month: string;
  date_of_credit: string;
  no_of_employees: number;
  amount: number;
}

interface EpfoEstablishment {
  establishment_id: string;
  address: string;
  city: string;
  latest_date_of_credit: string;
  date_of_setup: string;
  establishment_name: string;
  exemption_status_edli: string;
  exemption_status_pension: string;
  exemption_status_pf: string;
  no_of_employees: number;
  principal_business_activities: string;
  amount: number;
  latest_wage_month: string;
  working_status: string;
  filing_details: EpfoFiling[];
}

interface EpfoTabProps {
  establishments: EpfoEstablishment[] | null;
}


const formatMonthYear = (month: number, year: number): string => {
  const monthNames = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
  ];
  
  return `${monthNames[month]}-${year % 100}`;
};

const generateMonthsForLast3Years = (): { month: number; year: number }[] => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  const months: { month: number; year: number }[] = [];
  
  // Generate 36 months (3 years) back from current
  for (let i = 0; i < 36; i++) {
    let monthToAdd = currentMonth - i;
    let yearToAdd = currentYear;
    
    while (monthToAdd < 0) {
      monthToAdd += 12;
      yearToAdd -= 1;
    }
    
    months.push({ month: monthToAdd, year: yearToAdd });
  }
  
  return months.reverse();
};

const isFilingOnTime = (filingDate: Date): boolean => {
  const dueDate = new Date(filingDate);
  dueDate.setMonth(dueDate.getMonth() + 1);
  dueDate.setDate(15);
  
  return filingDate <= dueDate;
};

const getFilingStatusForMonth = (establishments: EpfoEstablishment[], targetMonth: number, targetYear: number): { 
  status: "on-time" | "delayed" | "default" | "unavailable"; 
  delayCount?: number;
  employeeCount: number;
} => {
  // Format month-year string like "JAN-25"
  const monthNames = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
  ];
  
  const targetMonthStr = `${monthNames[targetMonth]}-${targetYear % 100}`;
  
  let filings: EpfoFiling[] = [];
  let totalEmployees = 0;
  
  // Find relevant filings across all establishments
  establishments.forEach(establishment => {
    const matchingFilings = establishment.filing_details.filter(
      filing => filing.wage_month === targetMonthStr
    );
    
    if (matchingFilings.length > 0) {
      filings = filings.concat(matchingFilings);
      
      // Sum up the employee count for this month
      totalEmployees += matchingFilings.reduce((sum, filing) => sum + filing.no_of_employees, 0);
    }
  });
  
  if (filings.length === 0) {
    // Check if this is a future month
    const currentDate = new Date();
    const targetDate = new Date(targetYear, targetMonth);
    
    if (targetDate > currentDate) {
      return { status: "unavailable", employeeCount: 0 };
    }
    return { status: "default", employeeCount: 0 };
  }
  
  // Get the latest filing date for each establishment for this month
  const latestFilings = filings.reduce((acc, filing) => {
    const filingDate = new Date(filing.date_of_credit);
    if (!acc[filing.trrn] || filingDate > new Date(acc[filing.trrn].date_of_credit)) {
      acc[filing.trrn] = filing;
    }
    return acc;
  }, {} as Record<string, EpfoFiling>);
  
  // Check if all filings are on time
  const lateFilings = Object.values(latestFilings).filter(filing => {
    const filingDate = new Date(filing.date_of_credit);
    return !isFilingOnTime(filingDate);
  });
  
  if (lateFilings.length === 0) {
    return { status: "on-time", employeeCount: totalEmployees };
  } else {
    return { 
      status: "delayed", 
      delayCount: lateFilings.length,
      employeeCount: totalEmployees
    };
  }
};

const calculateOnTimePercentage = (establishments: EpfoEstablishment[]): number => {
  const months = generateMonthsForLast3Years();
  
  let totalFilings = 0;
  let onTimeFilings = 0;
  
  months.forEach(({ month, year }) => {
    const status = getFilingStatusForMonth(establishments, month, year);
    
    if (status.status !== "unavailable") {
      totalFilings++;
      if (status.status === "on-time") {
        onTimeFilings++;
      }
    }
  });
  
  return totalFilings > 0 ? Math.round((onTimeFilings / totalFilings) * 100) : 0;
};

const getDateRangeText = () => {
  const currentDate = new Date();
  const threeYearsAgo = new Date();
  threeYearsAgo.setFullYear(currentDate.getFullYear() - 2);
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  return `${monthNames[threeYearsAgo.getMonth()]}'${String(threeYearsAgo.getFullYear() % 100)} - ${monthNames[currentDate.getMonth()]}'${String(currentDate.getFullYear() % 100)}`;
};

const EpfoTab: React.FC<EpfoTabProps> = ({ establishments }) => {
  const months = useMemo(() => generateMonthsForLast3Years(), []);
  const epfOnTimePercentage = useMemo(() => calculateOnTimePercentage(establishments || []), [establishments]);
  
  // Prepare data for the line chart
  const chartData = useMemo(() => {
    return months.map(({ month, year }) => {
      const status = getFilingStatusForMonth(establishments || [], month, year);
      return {
        month: formatMonthYear(month, year),
        employees: status.employeeCount
      };
    }).filter(data => data.employees > 0);
  }, [establishments, months]);
  
  if (!establishments || establishments.length === 0 || establishments.every(e => !e.filing_details || e.filing_details.length === 0) ) {
    return (
      <div className="p-6 text-center text-gray-500">
        No EPFO records available for this company.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Summary Card */}
      <Card className="bg-white">
        <CardContent className="p-6">
          <h3 className="text-sm text-gray-600">On time EPF Filing</h3>
          <div className="flex items-baseline mt-1">
            <span className="text-4xl font-bold">{epfOnTimePercentage}%</span>
            <span className="ml-2 text-gray-500 text-sm">{getDateRangeText()}</span>
          </div>
        </CardContent>
      </Card>

      {/* Filing Heatmap */}
      <section>
        <div className="mb-4">
          <h3 className="text-lg font-medium flex items-center">
            EPF Filing Status
            <span className="inline-block ml-1 text-gray-400 cursor-help">ⓘ</span>
          </h3>
          <div className="flex items-center text-gray-500 text-sm mt-1">
            <span className="font-medium text-black">{epfOnTimePercentage}%</span>
            <span className="ml-2">EPF filed on time from {getDateRangeText()}</span>
          </div>
        </div>

        <div className="mb-3 flex items-center justify-end space-x-6 text-sm">
          <div className="flex items-center">
            <span className="w-3 h-0.5 bg-gray-300 inline-block mr-2"></span>
            <span className="text-gray-500">Data Unavailable</span>
          </div>
          <div className="flex items-center">
            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
            <span className="text-gray-500">On-Time</span>
          </div>
          <div className="flex items-center">
            <AlertCircle className="h-4 w-4 text-amber-500 mr-2" />
            <span className="text-gray-500">Delayed</span>
          </div>
          {/* <div className="flex items-center">
            <MinusCircle className="h-4 w-4 text-red-500 mr-2" />
            <span className="text-gray-500">Default</span>
          </div> */}
        </div>

        <div className="overflow-x-auto">
          {/* Group months by year */}
          {(() => {
            // Group months by year
            const yearGroups = months.reduce((groups, { month, year }) => {
              if (!groups[year]) {
                groups[year] = [];
              }
              groups[year].push(month);
              return groups;
            }, {} as Record<number, number[]>);
            
            return (
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="border-b text-sm">
                    <th className="py-2 px-3 text-left font-medium"></th>
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => (
                      <th key={month} className="py-2 px-3 text-center font-medium">{month}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(yearGroups).map(([year, _]) => (
                    <tr key={year} className="border-b">
                      <td className="py-3 px-3 font-medium">{year}</td>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((_, index) => {
                        const status = getFilingStatusForMonth(establishments, index, parseInt(year));

                        return (
                          <td key={`${year}-${index}`} className="py-3 px-3 text-center">
                            {status.status === "on-time" && <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />}
                            {status.status === "delayed" && <span className="text-amber-500 font-medium">{status.delayCount}</span>}
                            {status.status === "default" && <MinusCircle className="h-5 w-5 text-red-500 mx-auto" />}
                            {status.status === "unavailable" && <span className="inline-block w-3 h-0.5 bg-gray-300"></span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </div>
      </section>

      {/* Employees Line Graph */}
      <section>
        <h3 className="text-lg font-medium mb-4">Employee Count Over Time</h3>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                angle={-45}
                textAnchor="end"
                height={50}
                tick={{ fontSize: 12 }}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="employees" 
                name="Number of Employees"
                stroke="#8884d8" 
                strokeWidth={2}
                activeDot={{ r: 8 }} 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
};

export default EpfoTab;
